import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Sparkles, Award, Building2, Users } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CourseCard from "../components/CourseCard";
import { PromoBanner, SponsorBanner } from "../components/PublicBanners";
import useScrollReveal from "../hooks/useScrollReveal";

/**
 * Shared audience landing template.
 *
 * Props:
 *   - audienceSlug: "automotive" | "general_business"
 *   - landingParam: "automotive" | "entrepreneurs" | "business" — used for ?landing= analytics
 *   - registerAudienceParam: "automotive" | "entrepreneur" | "business" — passed to /register
 *     (frontend maps entrepreneur|business → backend general_business)
 *   - hero: { overline, headline (JSX allowed), subhead, heroImage }
 *   - stats: [{ icon, value, label }, ...] (3 items)
 *   - testimonials: [{ name, role, quote }, ...]
 *   - featuredHeadline: JSX
 *   - howItWorksSteps: optional override [{ title, body }]
 *   - testidPrefix: unique testid prefix for the page (e.g., "automotive-landing")
 */
export default function AudienceLanding({
  audienceSlug,
  landingParam,
  registerAudienceParam,
  hero,
  stats,
  testimonials,
  featuredHeadline,
  howItWorksSteps,
  testidPrefix = "audience-landing",
}) {
  const ref = useScrollReveal();
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/courses?audience=${audienceSlug}`).then(r => r.data).catch((e) => { console.error(e); return []; }),
      api.get("/instructors").then(r => r.data).catch((e) => { console.error(e); return []; }),
      api.get(`/plans?audience=${audienceSlug}`).then(r => r.data).catch((e) => { console.error(e); return []; }),
    ]).then(([c, ins, pl]) => {
      setCourses(c);
      setInstructors(ins);
      setPlans(pl);
    });
  }, [audienceSlug]);

  const featured = courses.filter((c) => c.is_featured).slice(0, 3);
  const previewSlug = featured[0]?.slug || courses[0]?.slug;

  const defaultSteps = [
    { title: "Pick your audience", body: "Choose the track built for your world — dealership, startup, or business team." },
    { title: "Learn from operators", body: "Real teachers. Real wins. No theory." },
    { title: "Build real projects", body: "Walk away with shippable AI workflows, not slides." },
    { title: "Earn certificates", body: "Prove what you've learned with verifiable credentials." },
  ];
  const steps = howItWorksSteps || defaultSteps;

  return (
    <div ref={ref} className="bg-fc-navy min-h-screen text-white" data-testid={`${testidPrefix}-page`}>
      <Navbar promoLocation="homepage" />
      <SponsorBanner location="homepage" />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center pt-20" data-testid={`${testidPrefix}-hero`}>
        <div className="absolute inset-0">
          <img src={hero.heroImage} alt={audienceSlug} className="w-full h-full object-cover" />
          <div className="absolute inset-0 fc-hero-overlay" />
          <div className="absolute inset-0 fc-grid-pattern opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 py-16">
          <div className="lg:col-span-8 reveal">
            <div className="fc-overline mb-6">{hero.overline}</div>
            <h1 className="fc-h-hero text-4xl sm:text-5xl lg:text-7xl xl:text-8xl mb-6">
              {hero.headline}
            </h1>
            <p className="text-white/70 text-lg sm:text-xl max-w-2xl leading-relaxed mb-10">
              {hero.subhead}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to={previewSlug ? `/courses/${previewSlug}/preview` : "/courses"}
                className="btn-fc-red"
                data-testid={`${testidPrefix}-cta-preview`}
              >
                <PlayCircle size={18} /> Start Free Lesson
              </Link>
              <Link
                to={`/register?audience=${registerAudienceParam}&landing=${landingParam}`}
                className="btn-fc-outline"
                data-testid={`${testidPrefix}-cta-register`}
              >
                Get Complimentary Access <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex lg:col-span-4 items-end justify-end">
            <div className="border-l border-white/10 pl-8 max-w-xs reveal">
              <Sparkles className="text-fc-red mb-4" size={28} />
              <p className="text-white/80 leading-relaxed text-sm">
                "Operator-led AI training. The exact playbooks that work in the real world."
              </p>
              <p className="mt-4 text-xs tracking-[0.18em] text-white/50">— THE FREDDY TEAM</p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-[#060A24] border-y border-white/10" data-testid={`${testidPrefix}-stats`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid sm:grid-cols-3 gap-6 sm:gap-12 text-center sm:text-left">
          {stats.map((s, i) => {
            const Icon = s.icon || Sparkles;
            return (
              <div key={i} className="flex items-center gap-4 justify-center sm:justify-start">
                <Icon className="text-fc-red" size={26} />
                <div>
                  <p className="text-2xl font-black tracking-tight">{s.value}</p>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/60">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-fc-navy py-24" data-testid={`${testidPrefix}-how-it-works`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12 reveal">
            <div className="fc-overline mb-3">HOW IT WORKS</div>
            <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              From watching to <span className="text-fc-red">shipping</span> in four steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 reveal">
            {steps.map((s, i) => (
              <div key={i} className="bg-[#060A24] border border-white/5 p-8" data-testid={`${testidPrefix}-step-${i + 1}`}>
                <div className="w-10 h-10 rounded-full bg-fc-red flex items-center justify-center font-black text-white mb-5">
                  {i + 1}
                </div>
                <h3 className="font-black text-base uppercase tracking-wide mb-2">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="bg-[#060A24] py-24" data-testid={`${testidPrefix}-featured`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 reveal">
            <div>
              <div className="fc-overline mb-3">FEATURED PROGRAMS</div>
              <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl">{featuredHeadline}</h2>
            </div>
            <Link to={`/courses?audience=${audienceSlug}`} className="text-sm font-bold tracking-wider text-white/80 hover:text-fc-red flex items-center gap-1 self-start sm:self-auto">
              VIEW ALL COURSES <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
            {featured.length > 0 ? featured.map((c) => (
              <CourseCard key={c.id} course={c} featured />
            )) : courses.slice(0, 3).map((c) => (
              <CourseCard key={c.id} course={c} featured />
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTORS */}
      <section className="bg-fc-navy py-24" data-testid={`${testidPrefix}-instructors`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 reveal">
            <div>
              <div className="fc-overline mb-3">TAUGHT BY OPERATORS</div>
              <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl">
                People who <span className="text-fc-red">actually ship.</span>
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
            {instructors.slice(0, 3).map((ins) => (
              <Link
                key={ins.id} to={`/instructors/${ins.id}`}
                className="bg-[#060A24] border border-white/5 hover:border-fc-red/40 p-8 transition-all hover:-translate-y-1 group"
                data-testid={`${testidPrefix}-instructor-${ins.id}`}
              >
                <img src={ins.avatar_url} alt={ins.name} className="w-20 h-20 object-cover mb-5 grayscale group-hover:grayscale-0 transition-all" />
                <h3 className="text-xl font-black tracking-tight mb-1">{ins.name}</h3>
                <p className="text-fc-red text-xs font-bold tracking-[0.18em] uppercase mb-4">{ins.title}</p>
                <p className="text-white/60 text-sm leading-relaxed line-clamp-3">{ins.bio}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials && testimonials.length > 0 && (
        <section className="bg-[#060A24] py-24" data-testid={`${testidPrefix}-testimonials`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="mb-12 reveal">
              <div className="fc-overline mb-3">WHAT THEY SAY</div>
              <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl max-w-3xl">Results speak louder.</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 reveal">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-fc-navy border border-white/5 p-8" data-testid={`${testidPrefix}-testimonial-${i}`}>
                  <div className="text-fc-red text-4xl font-black leading-none mb-4">"</div>
                  <p className="text-white/85 leading-relaxed mb-6 text-sm">{t.quote}</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-bold text-sm">{t.name}</p>
                    <p className="text-white/50 text-xs tracking-wider">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING TEASER */}
      <section className="bg-fc-navy py-24" data-testid={`${testidPrefix}-pricing`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12 reveal">
            <div className="fc-overline mb-3">SIMPLE. ANNUAL.</div>
            <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl">
              Two plans. One <span className="text-fc-red">AI-first</span> playbook.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 reveal">
            {plans.map((p) => (
              <div key={p.id} className={`p-8 lg:p-10 bg-[#060A24] border ${p.is_recommended ? "border-fc-red border-2 relative overflow-hidden" : "border-white/10"}`} data-testid={`${testidPrefix}-plan-${p.slug}`}>
                {p.is_recommended && (
                  <div className="absolute top-0 right-0 bg-fc-red text-white text-[10px] tracking-[0.2em] font-bold px-3 py-1.5">MOST POPULAR</div>
                )}
                <p className="text-fc-red text-xs tracking-[0.2em] font-bold uppercase mb-3">{p.name}</p>
                <div className="flex items-end gap-2 mb-5">
                  <span className="text-5xl font-black tracking-tight">{p.price_display}</span>
                  <span className="text-white/50 text-sm pb-2">/year</span>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-6">{p.description}</p>
                <Link
                  to={`/register?plan=${p.slug}&audience=${registerAudienceParam}&landing=${landingParam}`}
                  className={p.is_recommended ? "btn-fc-red w-full justify-center" : "btn-fc-outline w-full justify-center"}
                  data-testid={`${testidPrefix}-plan-${p.slug}-cta`}
                >
                  Choose {p.name.split(" ")[0]}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
